<template>
  <component :is="tag" v-if="item" class="text-media-cta-section">
    <div class="text-media-cta-section-container">
      <StoryblokComponent
        :blok="item"
        :level="3"
        :size-hint="90"
        :size-hint-mobile="90"
        class="text-media-cta-section-item"
      />
    </div>
  </component>
</template>

<script setup>
const props = defineProps({
  blok: {
    type: Object,
    required: true,
  },
  tag: {
    type: String,
    required: false,
    default: "div",
  },
});

// The section hosts a single centered media-text-cta.
// Card color: yellow on white sections, white on alternate (yellow) sections,
// pink (solid-02) when the nested blok has is_special.
const item = computed(() => props.blok.items?.[0]);
</script>

<style lang="scss">
.background-text-media-cta-section {
  --parent-bg: var(--solid-07);
}

.text-media-cta-section .media-text-cta {
  @include pair-3;

  &.is-special {
    @include pair-2;
  }
}

.alternate .text-media-cta-section .media-text-cta:not(.is-special) {
  @include pair-7;
}

// Country exceptions: contrast of nested media-text-cta
:root.theme-nl {
  // Yellow card: black text + CTA
  .text-media-cta-section .media-text-cta:not(.is-special) {
    color: var(--text-100);

    .simple-button:not(.link) {
      color: var(--text-100);

      &:not(.round)::before {
        background: var(--text-100);
      }
    }
  }

  // Blue card: white text + CTA (must beat pair-7 on .background.alternate)
  .background .text-media-cta-section .media-text-cta.is-special,
  .alternate .text-media-cta-section .media-text-cta.is-special {
    color: var(--solid-07);

    .simple-button:not(.link):not(.round):not(.solid) {
      color: var(--solid-07);

      &::before {
        background: var(--solid-07);
      }
    }
  }
}

:root.theme-ie {
  // Light green card (default pair-3 is too dark): black text + CTA
  .text-media-cta-section .media-text-cta:not(.is-special) {
    background: var(--solid-01);
    color: var(--text-100);
    --parent-bg: var(--solid-01);

    .simple-button:not(.link) {
      color: var(--text-100);

      &:not(.round)::before {
        background: var(--text-100);
      }
    }
  }

  .alternate .text-media-cta-section .media-text-cta:not(.is-special) {
    background: var(--solid-07);
    --parent-bg: var(--solid-07);
  }

  .text-media-cta-section .media-text-cta.is-special {
    color: var(--text-100);

    .simple-button:not(.link) {
      color: var(--text-100);

      &:not(.round)::before {
        background: var(--text-100);
      }
    }
  }
}

:root.theme-cl {
  // Is special: very light orange + black text + CTA
  .text-media-cta-section .media-text-cta.is-special {
    background: var(--solid-01);
    color: var(--text-100);
    --parent-bg: var(--solid-01);

    .simple-button:not(.link) {
      color: var(--text-100);

      &:not(.round)::before {
        background: var(--text-100);
      }
    }
  }
}

:root.theme-de {
  // Orange card: black text + CTA
  .text-media-cta-section .media-text-cta.is-special {
    color: var(--text-100);

    .simple-button:not(.link) {
      color: var(--text-100);

      &:not(.round)::before {
        background: var(--text-100);
      }
    }
  }

  // White card: restore CTA outline (pair-3 leak makes it white-on-white)
  .alternate .text-media-cta-section .media-text-cta:not(.is-special) {
    color: var(--text-100);

    .simple-button:not(.link) {
      color: var(--text-100);

      &:not(.round)::before {
        background: var(--text-100);
      }
    }
  }
}
</style>

<style lang="scss" scoped>
.text-media-cta-section {
  position: relative;
}

.text-media-cta-section-container {
  grid-column: 2 / -2;
  overflow: auto;

  @include for-desktop-up {
    grid-column: 5 / -5;
  }
}

.text-media-cta-section-item {
  width: 100%;

  :deep(.title) {
    @include size-m;
  }
  :deep(.text-wrapper.size-s) {
    font-size: 1rem;
  }

  @include for-desktop-up {
    :deep(.media),
    :deep(.collection-carousel img),
    :deep(.collection-carousel .youtube-player) {
      max-height: 70vh;
    }
    :deep(.media:not(.youtube-player)),
    :deep(.collection-carousel img),
    :deep(.collection-carousel .youtube-player) {
      aspect-ratio: 2/1 !important;
    }
  }
}
</style>
